import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getToken } from "../utils/auth";
import QuestionAnalysis from "./QuestionAnalysis";
import "./AssignmentReportPage.css";

export default function AssignmentReportPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const { assignmentId, college, colleges } = state || {};

  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ---------- FILTER STATES ---------- */
  const [selectedBatch, setSelectedBatch] = useState("ALL");
  const [selectedCollege, setSelectedCollege] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "rollNo", direction: "asc" });

  /* ---------- FETCH REPORT ---------- */
  useEffect(() => {
    const collegeList = Array.isArray(colleges) && colleges.length > 0
      ? colleges
      : college ? [college] : [];

    if (!assignmentId || collegeList.length === 0) {
      navigate(-1);
      return;
    }

    async function fetchReport() {
      setLoading(true);
      try {
        const fetchPromises = collegeList.map(async (collegeName) => {
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/assignment-submissions/getSubmissionByCollegeAndAssignment`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${getToken()}`
                },
                body: JSON.stringify({ assignmentId, college: collegeName })
              }
            );
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            
            const submissionArray = Array.isArray(data) ? data : data?.submissions || [];
            
            // Attach specific college to each submission record
            return submissionArray.map(sub => ({
              ...sub,
              college: sub.college || sub.collegeName || collegeName
            }));
          } catch (err) {
            console.error(`Failed to fetch report for college ${collegeName}:`, err);
            return [];
          }
        });

        const results = await Promise.all(fetchPromises);
        const submissionMap = new Map();

        results.flat().forEach((submission) => {
          const key = submission._id || `${submission.rollNo}-${submission.studentName}-${submission.college}`;
          submissionMap.set(key, submission);
        });

        setReport(Array.from(submissionMap.values()));
      } catch (err) {
        console.error("Failed to fetch assignment report:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchReport();
  }, [assignmentId, college, colleges, navigate]);

  /* ---------- DISPLAY TITLE/BADGE ---------- */
  const displayCollege = useMemo(() => {
    if (Array.isArray(colleges) && colleges.length > 0) {
      return colleges.length > 2 
        ? `${colleges.slice(0, 2).join(", ")} +${colleges.length - 2} more`
        : colleges.join(", ");
    }
    return college || "Campus Overview";
  }, [college, colleges]);

  /* ---------- UNIQUE BATCHES & COLLEGES ---------- */
  const batches = useMemo(() => {
    const set = new Set(report.map(r => r.batch).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [report]);

  const uniqueColleges = useMemo(() => {
    const set = new Set(report.map(r => r.college).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [report]);

  /* ---------- SORT & FILTER DATA ---------- */
  const filteredReport = useMemo(() => {
    let result = report.filter(r => {
      const batchMatch = selectedBatch === "ALL" || r.batch === selectedBatch;
      const collegeMatch = selectedCollege === "ALL" || r.college === selectedCollege;
      const query = searchText.trim().toLowerCase();
      
      if (!query) return batchMatch && collegeMatch;

      const nameMatch = r.studentName?.toLowerCase().includes(query);
      const rollMatch = r.rollNo?.toLowerCase().includes(query);
      const collegeNameMatch = r.college?.toLowerCase().includes(query);

      return batchMatch && collegeMatch && (nameMatch || rollMatch || collegeNameMatch);
    });

    if (sortConfig.key) {
      result.sort((a, b) => {
        let valA = a[sortConfig.key] ?? "";
        let valB = b[sortConfig.key] ?? "";

        if (typeof valA === "string") valA = valA.toLowerCase();
        if (typeof valB === "string") valB = valB.toLowerCase();

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [report, selectedBatch, selectedCollege, searchText, sortConfig]);

  /* ---------- SUMMARY METRICS ---------- */
  const summary = useMemo(() => {
    const total = filteredReport.length;
    if (total === 0) return { total: 0, avgSolved: "0.0", maxSolved: 0 };

    const sum = filteredReport.reduce((acc, r) => acc + (Number(r.problemsSolved) || 0), 0);
    const max = Math.max(...filteredReport.map(r => Number(r.problemsSolved) || 0));

    return {
      total,
      avgSolved: (sum / total).toFixed(1),
      maxSolved: max
    };
  }, [filteredReport]);

  /* ---------- SORT HANDLER ---------- */
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  /* ---------- DOWNLOAD EXCEL ---------- */
  const buildExportRows = rows =>
    rows.map(r => ({
      "Roll No": r.rollNo || "N/A",
      "Student Name": r.studentName || "Unknown",
      "College": r.college || "N/A",
      "Batch": r.batch || "N/A",
      "Problems Solved": r.problemsSolved ?? 0,
      "Submitted At": r.createdAt ? new Date(r.createdAt).toLocaleString() : "N/A"
    }));

  const exportWorkbook = (rows, fileName) => {
    if (!rows.length) return;
    const worksheet = XLSX.utils.json_to_sheet(buildExportRows(rows));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Assignment Report");
    XLSX.writeFile(workbook, fileName);
  };

  const downloadFilteredExcel = () => {
    const sanitizedBatch = selectedBatch.replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = selectedBatch === "ALL"
      ? "Assignment_Report_Filtered.xlsx"
      : `Assignment_Report_${sanitizedBatch}.xlsx`;
    exportWorkbook(filteredReport, fileName);
  };

  const downloadBatchExcel = () => {
    const batchRows = report.filter(
      r => selectedBatch === "ALL" || r.batch === selectedBatch
    );
    const sanitizedBatch = selectedBatch.replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = selectedBatch === "ALL"
      ? "Assignment_Report_All_Batches.xlsx"
      : `Assignment_Report_Batch_${sanitizedBatch}.xlsx`;

    exportWorkbook(batchRows, fileName);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime())
      ? "—"
      : parsed.toLocaleString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true
        });
  };

  return (
    <div className="report-container">
      {/* HEADER SECTION */}
      <header className="report-header">
        <div className="header-titles">
          <div className="header-badge-row">
            <span className="badge-pill" title={Array.isArray(colleges) ? colleges.join(", ") : college}>
              {displayCollege}
            </span>
            <span className="badge-subhead">Performance Ledger</span>
          </div>
          <h2>Assignment Evaluation Report</h2>
          <p>Detailed performance records and score distribution by student</p>
        </div>
        <button
          className={`toggle-analysis-btn ${showAnalysis ? "active" : ""}`}
          onClick={() => setShowAnalysis(prev => !prev)}
        >
          {showAnalysis ? "Hide Analytics" : "Question Analytics"}
        </button>
      </header>

      {/* SUMMARY KPI CARDS */}
      <section className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-label">Submissions Logged</span>
            <span className="metric-icon">📄</span>
          </div>
          <div className="metric-value">{summary.total}</div>
          <span className="metric-subtext">Filtered student records</span>
        </div>

        <div className="metric-card info">
          <div className="metric-header">
            <span className="metric-label">Avg. Score / Student</span>
            <span className="metric-icon">📊</span>
          </div>
          <div className="metric-value">{summary.avgSolved}</div>
          <span className="metric-subtext">Mean problems solved</span>
        </div>

        <div className="metric-card success">
          <div className="metric-header">
            <span className="metric-label">Peak Performance</span>
            <span className="metric-icon">🎯</span>
          </div>
          <div className="metric-value">{summary.maxSolved}</div>
          <span className="metric-subtext">Max problems solved</span>
        </div>
      </section>

      {/* ANALYSIS CHART SECTION */}
      {showAnalysis && (
        <section className="analysis-panel">
          <QuestionAnalysis reportData={filteredReport} />
        </section>
      )}

      {/* CONTROLS / FILTERS BAR */}
      <div className="controls-bar">
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" width="18" height="18">
            <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name, roll no, or college..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {searchText && (
            <button className="clear-btn" onClick={() => setSearchText("")} aria-label="Clear search">×</button>
          )}
        </div>

        <div className="actions-group">
          {uniqueColleges.length > 2 && (
            <div className="select-wrapper">
              <select
                value={selectedCollege}
                onChange={e => setSelectedCollege(e.target.value)}
              >
                {uniqueColleges.map(c => (
                  <option key={c} value={c}>
                    {c === "ALL" ? "All Colleges" : `College: ${c}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="select-wrapper">
            <select
              value={selectedBatch}
              onChange={e => setSelectedBatch(e.target.value)}
            >
              {batches.map(b => (
                <option key={b} value={b}>
                  {b === "ALL" ? "All Batches" : `Batch: ${b}`}
                </option>
              ))}
            </select>
          </div>

          <button 
            className="export-btn primary" 
            onClick={downloadFilteredExcel}
            disabled={filteredReport.length === 0}
          >
            <span className="btn-icon">📥</span> Export Filtered
          </button>
          <button 
            className="export-btn secondary" 
            onClick={downloadBatchExcel}
            disabled={report.length === 0}
          >
            <span className="btn-icon">📁</span> Export Batch
          </button>
        </div>
      </div>

      {/* MAIN DATA TABLE / STATES */}
      {loading ? (
        <div className="state-card loading">
          <div className="spinner"></div>
          <p>Compiling student report records...</p>
        </div>
      ) : filteredReport.length === 0 ? (
        <div className="state-card empty">
          <div className="empty-icon">📂</div>
          <h4>No Records Matched</h4>
          <p>No student submissions match your query. Try resetting your search filters.</p>
        </div>
      ) : (
        <div className="table-card">
          <div className="table-responsive">
            <table className="report-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("rollNo")} className="sortable-header">
                    Roll No {sortConfig.key === "rollNo" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => handleSort("studentName")} className="sortable-header">
                    Student Name {sortConfig.key === "studentName" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => handleSort("college")} className="sortable-header">
                    College {sortConfig.key === "college" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => handleSort("batch")} className="sortable-header">
                    Batch {sortConfig.key === "batch" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => handleSort("problemsSolved")} className="text-center sortable-header">
                    Problems Solved {sortConfig.key === "problemsSolved" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => handleSort("createdAt")} className="text-right sortable-header">
                    Submitted At {sortConfig.key === "createdAt" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredReport.map((r, i) => (
                  <tr key={r._id || `${r.rollNo}-${i}`}>
                    <td className="font-mono">{r.rollNo || "—"}</td>
                    <td className="font-semibold text-main">{r.studentName || "N/A"}</td>
                    <td>
                      <span className="college-badge">{r.college || "N/A"}</span>
                    </td>
                    <td>
                      <span className="batch-badge">{r.batch || "N/A"}</span>
                    </td>
                    <td className="text-center">
                      <span className={`score-badge ${r.problemsSolved === 0 ? "zero" : ""}`}>
                        {r.problemsSolved ?? 0}
                      </span>
                    </td>
                    <td className="text-right text-muted font-time">
                      {formatDate(r.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            Showing <strong>{filteredReport.length}</strong> of <strong>{report.length}</strong> total entries
          </div>
        </div>
      )}
    </div>
  );
}